/**
 * SyncEngine — pending operatsiyalarni server'ga jo'natish
 *
 * Strategiya:
 *   1. pendingOps jadvalidan barcha 'pending' operatsiyalarni olish
 *   2. Har birini ketma-ket jo'natish (parallel emas — order saqlanadi)
 *   3. Muvaffaqiyatli → status='synced' (yoki o'chirish)
 *   4. Xato → attempts++
 *      - attempts < 5: status='pending', exponential backoff
 *      - attempts >= 5: → DLQ (dead letter queue)
 *   5. Network error → barcha operatsiyalarni keyingi sync'ga qoldirish
 *
 * Conflict resolution:
 *   - Sotuvlar (orders) — ADDITIVE: lokal sotuvlar yo'qolmaydi (har biri unique offlineId)
 *   - Mahsulotlar — SERVER WINS (LWW)
 *   - Inventar — ADDITIVE MERGE (Faza 9 maxsus logic, hozir yo'q)
 */

import type { CreateSaleInput, PendingOperation, SyncStatus } from '@avtofix/shared';

import { ApiClientError, apiClient } from '@/shared/lib/api-client';

import { db } from './dexie';

const MAX_ATTEMPTS = 5;

export interface SyncResult {
  total: number;
  synced: number;
  failed: number;
  movedToDlq: number;
  errors: { id: string; error: string }[];
}

class SyncEngine {
  private isSyncing = false;
  private listeners = new Set<(result: SyncResult) => void>();

  /**
   * Sync ishlayotganmi (parallel call'lardan saqlash)
   */
  isRunning(): boolean {
    return this.isSyncing;
  }

  /**
   * Subscribe — har sync yakunida xabar
   */
  subscribe(listener: (result: SyncResult) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Asosiy sync funksiyasi
   */
  async sync(): Promise<SyncResult> {
    if (this.isSyncing) {
      return { total: 0, synced: 0, failed: 0, movedToDlq: 0, errors: [] };
    }

    this.isSyncing = true;
    const result: SyncResult = {
      total: 0,
      synced: 0,
      failed: 0,
      movedToDlq: 0,
      errors: [],
    };

    try {
      const pending = await db.pendingOps.where('status').equals('pending').sortBy('createdAt');
      result.total = pending.length;

      for (const op of pending) {
        const success = await this.processOperation(op);
        if (success) {
          result.synced++;
        } else {
          // attempts++ va kerak bo'lsa DLQ
          const updated = await db.pendingOps.get(op.id);
          if (updated === undefined) {
            // DLQ ga ko'chgan
            result.movedToDlq++;
          } else {
            result.failed++;
          }
        }
      }
    } finally {
      this.isSyncing = false;
      this.listeners.forEach((l) => {
        l(result);
      });
    }

    return result;
  }

  /**
   * Bitta operatsiyani jo'natish
   */
  private async processOperation(op: PendingOperation): Promise<boolean> {
    // Status → syncing (UI uchun)
    await db.pendingOps.update(op.id, { status: 'syncing' as SyncStatus });

    try {
      await this.dispatchOperation(op);
      // Muvaffaqiyatli — o'chirish
      await db.pendingOps.delete(op.id);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Noma'lum xato";
      const newAttempts = op.attempts + 1;

      if (newAttempts >= MAX_ATTEMPTS) {
        // DLQ ga ko'chirish
        await db.moveToDeadLetter(op, errorMessage);
        return false;
      }

      // attempts++ va keyingi sync ga qoldirish
      await db.pendingOps.update(op.id, {
        status: 'failed' as SyncStatus,
        attempts: newAttempts,
        lastError: errorMessage,
      });

      // Re-queue qilish (status pending'ga qaytarish — keyingi sync'da qayta urinadi)
      // Exponential backoff: 2^attempts soniya kutib qaytarish
      const backoffMs = Math.min(60_000, 1000 * Math.pow(2, newAttempts));
      setTimeout(() => {
        void db.pendingOps.update(op.id, { status: 'pending' as SyncStatus });
      }, backoffMs);

      return false;
    }
  }

  /**
   * Operatsiyaga qarab API chaqirish
   */
  private async dispatchOperation(op: PendingOperation): Promise<void> {
    if (op.resource === 'order' && op.action === 'create') {
      const input = op.data as CreateSaleInput;
      // offlineId server tomonidan deduplication uchun ishlatiladi
      // Slow network (offline sync) uchun timeout 60s (default 30s yetmaydi)
      await apiClient.post('/orders', { ...input, offlineId: op.offlineId }, { timeout: 60_000 });
      return;
    }

    // Boshqa resourcelar (product, customer, debt) — kelajakda
    throw new ApiClientError({
      statusCode: 501,
      error: {
        code: 'NOT_IMPLEMENTED',
        message: `Operatsiya qo'llab-quvvatlanmaydi: ${op.resource} ${op.action}`,
      },
    });
  }

  /**
   * Pending operatsiyalar soni (UI uchun)
   */
  async getPendingCount(): Promise<number> {
    return db.pendingOps
      .where('status')
      .notEqual('syncing' as SyncStatus)
      .count();
  }

  /**
   * DLQ size (Sync Recovery UI uchun — Faza 9.5)
   */
  async getDlqCount(): Promise<number> {
    return db.deadLetterQueue.count();
  }
}

export const syncEngine = new SyncEngine();
